import { Comment } from 'antd';
import moment from 'moment';
import { parse } from 'query-string';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import {
  Approval as ApprovalType,
  ApprovalStatus,
  ApprovalObjectType,
} from 'api/approval';
import { ProjectId } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';
import { queueMacrotask } from 'utils/macrotask';
import { uid } from 'utils/uid';
import { ApprovalActions } from './approval-actions';
import { ApprovalComment } from './approval-comment';
import { CallToActions } from './approval-cta';
import { ApprovalForm } from './approval-form';
import { MessageEditor } from './message-editor';
import { MoreActionsMenu } from './more-actions-menu';

interface ApprovalProps {
  data: ApprovalType;
  onDelete: (approvalId: number) => Promise<unknown>;
  onSubmit: (payload: any, options: any) => void;
  isLoading: boolean;
  projectId: number;
}

export const Approval: FC<ApprovalProps> = (props) => {
  const { data, projectId, isLoading, onDelete, onSubmit } = props;
  const {
    data: { users },
  } = useProjectSquad(ProjectId(projectId));
  const userInfo = useUser();
  const currentUser = userInfo.user;
  const { requestBy, requestTo, comments = [] } = data;
  const requestedUser = users.find((u) => u?.userId === requestBy?.userId);
  const [approvalContent, setApprovalContent] = useState('');
  const [action, setAction] = useState<ApprovalStatus>();
  const { hash } = useLocation();
  const parsed = useMemo(() => parse(hash), [hash]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleCancel = () => {
    setAction(undefined);
    setApprovalContent('');
  };

  useEffect(() => {
    if (data.approvalId === Number(parsed.approval_id)) {
      queueMacrotask(() => {
        scrollRef?.current?.scrollIntoView({ behavior: 'smooth' });
      });
      setAction(ApprovalStatus.ChangeRequested);
    }
  }, [data.approvalId, parsed.approval_id]);

  const handleOk = () => {
    if (action) {
      const payload = {
        approvalId: data.approvalId,
        requestTo: [
          {
            userId: currentUser.userId,
            approvalStatus: action,
          },
        ],
        comment: {
          content: approvalContent,
        },
      };

      onSubmit(payload, {
        onSuccess: () => {
          setAction(undefined);
        },
      });
    }
  };
  const projectResource = useProjectAsRBACResource(projectId);
  const { hasAccessToResource } = useWithAccess();
  const hasAccessToDoAction =
    data.objectType === ApprovalObjectType.Document
      ? hasAccessToResource({
          // rule: 'DOCUMENT_APPROVAL_ACTIONS:WRITE',
          rule: 'DOCUMENT:READ',
          resourceData: projectResource,
        })
      : true;
  const hasAccessToDelete =
    data.objectType === ApprovalObjectType.Document
      ? hasAccessToResource({
          // rule: 'DOCUMENT_APPROVAL_ACTIONS:WRITE',
          rule: 'DOCUMENT:WRITE',
          resourceData: projectResource,
        })
      : true;

  const canDoApproval = Boolean(
    data.requestTo.filter(
      (r) =>
        r.userId === currentUser?.userId &&
        r.approvalStatus === ApprovalStatus.ApprovalRequested,
    ).length,
  );

  const updates = requestTo.filter(
    (r) => r.approvalStatus !== ApprovalStatus.ApprovalRequested,
  );

  const canShowMoreActionsMenu =
    Boolean(data.approvalId) && data.requestBy.userId === userInfo?.user.userId;

  return (
    <>
      <Comment
        datetime={
          data.createdAt && (
            <small>
              <HumanisedDate timestamp={moment(data.createdAt)} />
            </small>
          )
        }
        avatar={
          requestedUser && (
            <Avatar
              name={fullName(requestedUser)}
              shape="square"
              src={requestedUser.photoUrl}
              companyLogoUrl={requestedUser.company?.companyLogo}
            />
          )
        }
        author={
          requestedUser ? (
            <strong>{fullName(requestedUser)}</strong>
          ) : (
            <FormattedMessage id="approvals.requesting" />
          )
        }
        content={
          <>
            {hasAccessToDelete && canShowMoreActionsMenu && (
              <MoreActionsMenu
                onDelete={onDelete}
                approvalId={data.approvalId}
              />
            )}

            <ApprovalForm projectId={projectId} data={data} />
            {hasAccessToDoAction && canDoApproval && !action && (
              <ApprovalActions
                isLoading={isLoading}
                onApprove={() => {
                  setAction(ApprovalStatus.Approved);
                }}
                onRequestChanges={() => {
                  setAction(ApprovalStatus.ChangeRequested);
                }}
              />
            )}
          </>
        }
      />
      {action && (
        <div ref={scrollRef}>
          <Comment
            content={
              <>
                <MessageEditor
                  projectId={ProjectId(projectId)}
                  data={approvalContent}
                  onChange={setApprovalContent}
                />
                <CallToActions
                  loading={isLoading}
                  disableOk={false}
                  cancelText={<FormattedMessage id="cancel" />}
                  onCancel={handleCancel}
                  onOk={handleOk}
                  okText={
                    action === ApprovalStatus.ChangeRequested ? (
                      <FormattedMessage id="approvals.request_changes" />
                    ) : (
                      <FormattedMessage id="approvals.confirm" />
                    )
                  }
                />
              </>
            }
            avatar={
              currentUser && (
                <Avatar
                  name={fullName(currentUser)}
                  shape="square"
                  companyLogoUrl={currentUser.company?.companyLogo}
                  src={currentUser.photoUrl}
                />
              )
            }
          />
        </div>
      )}
      {updates.map((update, index) => (
        <ApprovalComment
          key={uid(String(index))}
          projectId={projectId}
          user={users.find((u) => u.userId === update.userId)}
          data={update}
          comment={comments?.find((c) => c.createdBy === update.userId)}
        />
      ))}
    </>
  );
};
